import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DRIVE_DATA_SOURCE, type DirectoryNode, type DriveDataSource } from '@mtm/drive/core';

import { PLATFORM_CHAT_ENTITY_TYPE } from '../components/platform-comments-chat/platform-chat-entity.const';
import { CryptoService } from '../services/crypto.service';
import { orgEntityPageForKind, type OrgAnchorIds, type OrgEntityPage, type OrgEntityTarget } from './org-entity-routes';

/** An anchor that belongs to an org page, with the address of that page. */
export interface OrgAnchorResolution {
  readonly page: OrgEntityPage;
  readonly target: OrgEntityTarget;
  /**
   * The ids the address was built from. `brand` is what tells a caller the anchor lives
   * under a brand other than the active one — the org pages are brand-scoped, and
   * `/workspaces` carries no brand in its URL at all, so reaching it needs the active
   * brand changed first.
   */
  readonly ids: OrgAnchorIds;
}

/** Kind name of a node — the dictionary's name, or the raw element type when it failed to resolve. */
function kindNameOf(node: DirectoryNode): string | null {
  return node.directoryKind?.directoryKindName ?? node.elementType ?? null;
}

/**
 * Answers "which page does this drive anchor live on?" for a bare directory uuid.
 *
 * A Recent activity draft row carries only `elementUuid` + `anchorKind: 'DIRECTORY'`, and
 * `projectUuid: null` for everything outside a project — an org entity's feed (brand /
 * workspace / campaign) and a My Own Space folder read identically. The org pages anchor
 * the comment shell on the entity's own uuid, so the anchor's drive KIND is what tells
 * them apart, and a campaign additionally needs its `WORKSPACE` ancestor for the `wid`
 * half of its URL. One `/ancestors` read supplies both.
 *
 * Returns null for every ordinary directory, which the callers read as "not an org page,
 * route it as before". A failed read also returns null: a redirect that falls back to the
 * old behaviour beats a dead click.
 */
@Injectable({ providedIn: 'root' })
export class DriveOrgAnchorResolver {
  private readonly dataSource: DriveDataSource = inject(DRIVE_DATA_SOURCE);
  private readonly crypto: CryptoService = inject(CryptoService);

  /** Anchors never change kind or parentage, so a hit is cached for the session. */
  private readonly resolved: Map<string, OrgAnchorResolution | null> = new Map<string, OrgAnchorResolution | null>();
  private readonly inFlight: Map<string, Promise<OrgAnchorResolution | null>> = new Map<
    string,
    Promise<OrgAnchorResolution | null>
  >();

  public resolve(elementUuid: string): Promise<OrgAnchorResolution | null> {
    const cached: OrgAnchorResolution | null | undefined = this.resolved.get(elementUuid);
    if (cached !== undefined) return Promise.resolve(cached);

    const pending: Promise<OrgAnchorResolution | null> | undefined = this.inFlight.get(elementUuid);
    if (pending !== undefined) return pending;

    const read: Promise<OrgAnchorResolution | null> = this.read(elementUuid).then(
      (resolution: OrgAnchorResolution | null) => {
        this.resolved.set(elementUuid, resolution);
        this.inFlight.delete(elementUuid);
        return resolution;
      },
      () => {
        // Not cached: a transient failure must not pin this anchor to "ordinary" for the session.
        this.inFlight.delete(elementUuid);
        return null;
      }
    );
    this.inFlight.set(elementUuid, read);
    return read;
  }

  private async read(elementUuid: string): Promise<OrgAnchorResolution | null> {
    const { ancestors, current } = await firstValueFrom(this.dataSource.getAncestors(elementUuid));
    const page: OrgEntityPage | null = orgEntityPageForKind(kindNameOf(current));
    if (page === null) return null;

    // The anchor counts as its own entity: a BRAND anchor has no BRAND ancestor, and its
    // `brand` is what tells the caller which brand must be active to reach `/workspaces`.
    // Ancestors come first so a real parent always wins over the anchor itself.
    const chain: ReadonlyArray<DirectoryNode> = [...ancestors, current];
    const ids: OrgAnchorIds = {
      self: elementUuid,
      brand: this.orgEntityUuid(chain, PLATFORM_CHAT_ENTITY_TYPE.BRAND),
      workspace: this.orgEntityUuid(chain, PLATFORM_CHAT_ENTITY_TYPE.WORKSPACE),
    };
    const target: OrgEntityTarget | null = page.target(ids, (value: string) => this.crypto.encryptString(value));
    return target === null ? null : { page, target, ids };
  }

  /**
   * Uuid of the node in `chain` whose kind maps to `entityType`, resolved through the same table.
   *
   * `elementUuid` — the PLATFORM entity the directory stands for, which is what `wid` /
   * `dc` carry. The org-hierarchy directories are uuid-pinned to their platform entity,
   * so it equals `directoryUuid` here (the lookup above already relies on that: the
   * anchor is read as a directory by the platform id the conversation stored).
   */
  private orgEntityUuid(chain: ReadonlyArray<DirectoryNode>, entityType: string): string | undefined {
    return chain.find((node: DirectoryNode) => orgEntityPageForKind(kindNameOf(node))?.entityType === entityType)
      ?.elementUuid;
  }
}
